'use client';
import React, { useState } from 'react';
import LoginForm from './LoginForm';
import SignUpForm from './SignUpForm';
import AppLogo from '@/components/ui/AppLogo';
import { Sparkles, Film, Zap, BarChart2 } from 'lucide-react';

const FEATURES = [
  { icon: Film, text: 'AI analyzes your full video and finds the best moments' },
  { icon: Sparkles, text: 'Generates 15s, 30s & 60s ad creatives automatically' },
  { icon: Zap, text: 'Burns your brand name & phone numbers into every export' },
  { icon: BarChart2, text: 'Publish directly to Facebook & Instagram Ads' },
];

export default function AuthScreen() {
  const [tab, setTab] = useState<'login' | 'signup'>('login');

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] auth-brand-panel flex-col justify-between p-10 xl:p-14 relative overflow-hidden">
        {/* Decorative blobs */}
        <div
          className="absolute top-[-80px] left-[-80px] w-72 h-72 rounded-full opacity-20 blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, #7C3AED, transparent 70%)' }}
          aria-hidden="true"
        />
        <div
          className="absolute bottom-[-60px] right-[-60px] w-64 h-64 rounded-full opacity-15 blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, #D946EF, transparent 70%)' }}
          aria-hidden="true"
        />

        {/* Logo */}
        <div className="flex items-center gap-3 relative z-10">
          <AppLogo size={40} />
          <div>
            <span className="text-gradient-primary font-bold text-xl tracking-tight">ShiporaX</span>
            <p className="text-muted-foreground text-xs">AI Ads Platform</p>
          </div>
        </div>

        {/* Hero copy */}
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 bg-primary/15 border border-primary/30 rounded-full px-3 py-1.5 mb-6">
            <Sparkles size={13} className="text-accent" />
            <span className="text-xs font-500 text-accent">Powered by AI</span>
          </div>
          <h1 className="text-3xl xl:text-4xl font-800 text-foreground leading-tight mb-4">
            Turn Any Video Into<br />
            <span className="text-gradient-primary">High-Converting Ads</span>
          </h1>
          <p className="text-muted-foreground text-base leading-relaxed mb-8 max-w-md">
            Upload your video. AI finds the best moments, creates short clips, adds your branding, and publishes to Meta — all automatically.
          </p>

          {/* Features */}
          <ul className="space-y-3">
            {FEATURES.map((f, i) => (
              <li key={`feature-${i}`} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/15 border border-primary/20 flex items-center justify-center shrink-0">
                  <f.icon size={15} className="text-primary" />
                </div>
                <span className="text-sm text-muted-foreground">{f.text}</span>
              </li>
            ))}
          </ul>

          {/* Social proof */}
          <div className="mt-8 flex items-center gap-4 pt-6 border-t border-border/50">
            <div className="flex -space-x-2">
              {['V', 'P', 'S', 'A'].map((initial, i) => (
                <div
                  key={`avatar-${i}`}
                  className="w-8 h-8 rounded-full border-2 border-background bg-gradient-primary flex items-center justify-center text-xs font-700 text-white"
                  style={{ transitionDelay: `${i * 50}ms` }}
                >
                  {initial}
                </div>
              ))}
            </div>
            <div>
              <p className="text-sm font-600 text-foreground">2,400+ businesses</p>
              <p className="text-xs text-muted-foreground">already creating ads with ShiporaX</p>
            </div>
          </div>
        </div>

        {/* Bottom stats */}
        <div className="relative z-10 grid grid-cols-3 gap-4">
          {[
            { value: '48K+', label: 'Ads Created' },
            { value: '₹2.1Cr', label: 'Ad Spend Managed' },
            { value: '3.2×', label: 'Avg ROAS' },
          ].map((stat) => (
            <div key={`stat-${stat.label}`} className="bg-muted/30 border border-border/50 rounded-xl p-3 text-center">
              <p className="text-xl font-700 text-gradient-primary tabular-nums">{stat.value}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right auth panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-10 xl:p-14 overflow-y-auto scrollbar-thin">
        {/* Mobile logo */}
        <div className="flex lg:hidden items-center gap-2 mb-8">
          <AppLogo size={32} />
          <span className="text-gradient-primary font-bold text-lg tracking-tight">ShiporaX AI Ads</span>
        </div>

        <div className="w-full max-w-md">
          {/* Tab switcher */}
          <div className="flex bg-muted/50 rounded-xl p-1 mb-8 border border-border">
            {(['login', 'signup'] as const).map((t) => (
              <button
                key={`tab-${t}`}
                onClick={() => setTab(t)}
                className={`flex-1 py-2.5 rounded-lg text-sm font-600 transition-all duration-200 ${
                  tab === t
                    ? 'bg-gradient-primary text-white shadow-lg'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {tab === 'login' ? (
            <LoginForm onSwitchToSignup={() => setTab('signup')} />
          ) : (
            <SignUpForm onSwitchToLogin={() => setTab('login')} />
          )}
        </div>
      </div>
    </div>
  );
}